import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { X, ShoppingBag, Check, ChevronLeft, ChevronRight, Mail, ZoomIn } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { useCart } from "@/lib/cart";
import { PURCHASING_ENABLED } from "@/lib/purchasing";
import { InquireModal } from "@/components/common/InquireModal";

export function ArtworkModal({ artwork, onClose }) {
  const [zoomed, setZoomed] = useState(false);
  const [origin, setOrigin] = useState("50% 50%");
  const [showHint, setShowHint] = useState(false);
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [inquireOpen, setInquireOpen] = useState(false);
  const [mobileZoomOpen, setMobileZoomOpen] = useState(false);
  const imgRef = useRef(null);
  const { addItem, isInCart } = useCart();
  const inCart = isInCart(artwork?.id);

  // Normalized image list: prefer artwork.images[] (new pieces schema), fall back
  // to single artwork.image for any legacy callers.
  const images = useMemo(() => {
    if (Array.isArray(artwork?.images) && artwork.images.length > 0) {
      return artwork.images.filter((i) => i && i.url);
    }
    return artwork?.image ? [{ url: artwork.image }] : [];
  }, [artwork]);

  const hasMultiple = images.length > 1;
  const activeImage = images[activeImageIdx] || images[0];
  const activeUrl = activeImage?.url || "";

  // Reset active image + zoom whenever the artwork itself changes.
  useEffect(() => {
    setActiveImageIdx(0);
    setZoomed(false);
    setOrigin("50% 50%");
    setMobileZoomOpen(false);
  }, [artwork?.id]);

  const goPrev = useCallback(() => {
    if (!hasMultiple) return;
    setActiveImageIdx((i) => (i - 1 + images.length) % images.length);
    setZoomed(false);
    setOrigin("50% 50%");
  }, [hasMultiple, images.length]);

  const goNext = useCallback(() => {
    if (!hasMultiple) return;
    setActiveImageIdx((i) => (i + 1) % images.length);
    setZoomed(false);
    setOrigin("50% 50%");
  }, [hasMultiple, images.length]);

  const handleEscape = useCallback((e) => {
    if (e.key === "Escape") {
      if (mobileZoomOpen) {
        setMobileZoomOpen(false);
      } else if (zoomed) {
        setZoomed(false);
      } else {
        onClose();
      }
    } else if (e.key === "ArrowLeft" && !zoomed && !mobileZoomOpen) {
      goPrev();
    } else if (e.key === "ArrowRight" && !zoomed && !mobileZoomOpen) {
      goNext();
    }
  }, [zoomed, mobileZoomOpen, onClose, goPrev, goNext]);

  useEffect(() => {
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [handleEscape]);

  const handleImageClick = () => {
    setZoomed((z) => !z);
    if (zoomed) setOrigin("50% 50%");
  };

  const handleMouseMove = (e) => {
    if (!zoomed || !imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setOrigin(`${x}% ${y}%`);
  };

  const handleMouseLeave = () => {
    if (zoomed) setOrigin("50% 50%");
  };

  if (!artwork) return null;

  // Extract medium from the first few words of description if it contains medium info
  const extractMedium = () => {
    if (artwork.medium) return artwork.medium;
    if (!artwork.description) return "";
    const desc = artwork.description;
    // Look for common medium patterns at the start of description
    const mediumMatch = desc.match(/^([A-Z][^.]{3,40}(?:on canvas|on paper|on board|on panel|media|sculpture|photography|installation|print|digital|wood|copper|bronze|acrylic|oil))/i);
    if (mediumMatch) return mediumMatch[1];
    // Fallback: use first short clause if it looks like a medium
    const firstPart = desc.split(".")[0];
    if (firstPart.length < 50) return firstPart;
    return "";
  };
  const medium = extractMedium();

  // Resolve the framed display label. The admin portal stores this as a
  // tri-state string ("yes" / "no" / ""); legacy records may still have
  // boolean `true`/`false`. Treat truthy boolean or "yes" as Framed,
  // "no" as Unframed, anything else as unset (we hide the row).
  const framedRaw = artwork.framed;
  let framedLabel = null;
  if (framedRaw === true || framedRaw === "yes") framedLabel = "Yes";
  else if (framedRaw === "no") framedLabel = "No";

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[90] bg-[#0A1128]/80 backdrop-blur-sm flex items-center justify-center md:p-4 lg:p-8"
        onClick={onClose}
        data-testid="artwork-modal-overlay"
      >
        <motion.div
          initial={{ scale: 0.97, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.97, opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          onClick={(e) => e.stopPropagation()}
          className="hidden md:flex bg-white flex-col lg:flex-row w-full max-w-[1400px] overflow-y-auto lg:overflow-hidden relative"
          style={{ maxHeight: "min(820px, calc(100vh - 4rem))" }}
          data-testid="artwork-modal-content"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 w-11 h-11 flex items-center justify-center bg-white/80 border border-[#0A1128]/15 text-[#0A1128] transition-all duration-300 hover:bg-[#0A1128] hover:text-white hover:rotate-90"
            data-testid="artwork-modal-close"
          >
            <X size={16} />
          </button>

          {/* Image side */}
          <div
            className="relative lg:flex-[1.5] bg-[#F8F9FA] flex flex-col overflow-hidden"
            data-testid="artwork-modal-image-container"
          >
            <div
              ref={imgRef}
              className="flex-1 flex items-center justify-center p-6 sm:p-8 lg:p-12 lg:w-full overflow-hidden relative"
              style={{ cursor: zoomed ? "zoom-out" : "zoom-in" }}
              onClick={handleImageClick}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              onMouseEnter={() => setShowHint(true)}
            >
              <img
                src={activeUrl}
                alt={`${artwork.title} by ${artwork.artist_name}${hasMultiple ? ` (view ${activeImageIdx + 1} of ${images.length})` : ""}`}
                className="object-contain transition-transform block max-w-full lg:max-h-full"
                style={{
                  transform: zoomed ? "scale(2.2)" : "scale(1)",
                  transformOrigin: origin,
                  transitionDuration: zoomed ? "80ms" : "300ms",
                }}
                draggable={false}
                data-testid="artwork-modal-image"
              />

              {/* Prev / Next arrows — only when multiple images and not zoomed */}
              {hasMultiple && !zoomed && (
                <>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); goPrev(); }}
                    aria-label="Previous image"
                    data-testid="artwork-modal-prev-image"
                    className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/85 border border-[#0A1128]/15 text-[#0A1128] transition-all duration-300 hover:bg-[#0A1128] hover:text-white"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); goNext(); }}
                    aria-label="Next image"
                    data-testid="artwork-modal-next-image"
                    className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/85 border border-[#0A1128]/15 text-[#0A1128] transition-all duration-300 hover:bg-[#0A1128] hover:text-white"
                  >
                    <ChevronRight size={16} />
                  </button>
                </>
              )}
            </div>

            {/* Thumbnail strip */}
            {hasMultiple && (
              <div
                className="flex-shrink-0 px-6 sm:px-8 lg:px-12 pb-5 pt-2 flex gap-2 overflow-x-auto"
                data-testid="artwork-modal-thumbnails"
                onClick={(e) => e.stopPropagation()}
              >
                {images.map((img, i) => {
                  const active = i === activeImageIdx;
                  return (
                    <button
                      key={img.url + i}
                      type="button"
                      onClick={() => {
                        setActiveImageIdx(i);
                        setZoomed(false);
                        setOrigin("50% 50%");
                      }}
                      aria-label={`View image ${i + 1}`}
                      data-testid={`artwork-modal-thumbnail-${i}`}
                      className={`relative flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 bg-white overflow-hidden border transition-all duration-200 ${
                        active
                          ? "border-[#CBA365] opacity-100 ring-1 ring-[#CBA365]"
                          : "border-[#0A1128]/15 opacity-75 hover:opacity-100"
                      }`}
                    >
                      <img
                        src={img.url}
                        alt=""
                        className="w-full h-full object-cover"
                        draggable={false}
                      />
                    </button>
                  );
                })}
              </div>
            )}

            {/* Zoom hint */}
            <span
              className="absolute bottom-4 left-4 text-[#8A94A6] transition-opacity duration-300 pointer-events-none"
              style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: "0.65rem",
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                opacity: !zoomed && showHint && !hasMultiple ? 0.7 : 0,
              }}
            >
              Click to zoom
            </span>
          </div>

          {/* Details side */}
          <div
            className="lg:min-w-[420px] lg:flex-1 p-6 md:p-8 lg:pt-12 lg:pr-10 lg:pl-10 overflow-y-auto"
            data-testid="artwork-modal-info"
          >
            {/* Artist eyebrow */}
            <p className="overline mb-4">{artwork.artist_name}</p>

            {/* Title */}
            <h2
              className="section-heading font-heading text-[#0A1128] mb-1"
              style={{ fontSize: "clamp(2.5rem, 3.5vw, 3.5rem)", lineHeight: 1 }}
            >
              {artwork.title}
            </h2>

            {/* Divider */}
            <div className="w-full h-[1px] bg-[rgba(10,25,41,0.1)] mt-7 mb-7" />

            {/* Description */}
            {artwork.description && (
              <div
                className="mb-8 pl-4 border-l border-[#CBA365]"
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  fontWeight: 300,
                  fontSize: "1rem",
                  lineHeight: 1.65,
                  color: "rgba(10, 25, 41, 0.75)",
                  textAlign: "left",
                }}
              >
                {artwork.description}
              </div>
            )}

            {/* Metadata rows */}
            <div className="mb-8">
              {medium && (
                <div
                  className="flex justify-between items-center py-3 border-t border-b"
                  style={{ borderColor: "rgba(10, 25, 41, 0.08)" }}
                >
                  <span
                    style={{
                      fontFamily: "'Outfit', sans-serif",
                      fontWeight: 500,
                      fontSize: "0.68rem",
                      letterSpacing: "0.28em",
                      textTransform: "uppercase",
                      color: "#CBA365",
                    }}
                  >
                    Medium
                  </span>
                  <span
                    className="font-heading italic"
                    style={{
                      fontSize: "1rem",
                      color: "rgba(10, 25, 41, 0.75)",
                      textTransform: "none",
                      WebkitTextStroke: "0.35px currentColor",
                      textShadow: "0.35px 0 0 currentColor",
                      letterSpacing: "0",
                      textAlign: "right",
                    }}
                  >
                    {medium}
                  </span>
                </div>
              )}
              {artwork.dimensions && (
                <div
                  className="flex justify-between items-center py-3 border-b"
                  style={{ borderColor: "rgba(10, 25, 41, 0.08)" }}
                >
                  <span
                    style={{
                      fontFamily: "'Outfit', sans-serif",
                      fontWeight: 500,
                      fontSize: "0.68rem",
                      letterSpacing: "0.28em",
                      textTransform: "uppercase",
                      color: "#CBA365",
                    }}
                  >
                    Dimensions
                  </span>
                  <span
                    className="font-heading italic"
                    style={{
                      fontSize: "1rem",
                      color: "rgba(10, 25, 41, 0.75)",
                      textTransform: "none",
                      WebkitTextStroke: "0.35px currentColor",
                      textShadow: "0.35px 0 0 currentColor",
                      letterSpacing: "0",
                      textAlign: "right",
                    }}
                  >
                    {artwork.dimensions}
                  </span>
                </div>
              )}
              <div
                className="flex justify-between items-center py-3 border-b"
                style={{ borderColor: "rgba(10, 25, 41, 0.08)", display: framedLabel ? undefined : "none" }}
                data-testid="modal-framed-row"
              >
                <span
                  style={{
                    fontFamily: "'Outfit', sans-serif",
                    fontWeight: 500,
                    fontSize: "0.68rem",
                    letterSpacing: "0.28em",
                    textTransform: "uppercase",
                    color: "#CBA365",
                  }}
                >
                  Framed
                </span>
                <span
                  className="font-heading italic"
                  style={{
                    fontSize: "1rem",
                    color: "rgba(10, 25, 41, 0.75)",
                    textTransform: "none",
                    WebkitTextStroke: "0.35px currentColor",
                    textShadow: "0.35px 0 0 currentColor",
                    letterSpacing: "0",
                    textAlign: "right",
                  }}
                >
                  {framedLabel}
                </span>
              </div>
            </div>

            {/* Price & CTA */}
            {artwork.sold ? (
              <div
                className="w-full py-4 text-center bg-[#F8F9FA] text-[#8A94A6] text-xs uppercase tracking-[0.2em]"
                data-testid="modal-sold-indicator"
              >
                Sold
              </div>
            ) : (
              <>
                <p
                  className="font-heading text-[#0A1128] text-left mb-5"
                  style={{ fontSize: "2rem", textTransform: "none", WebkitTextStroke: "0", textShadow: "none", letterSpacing: "0" }}
                  data-testid="modal-artwork-price"
                >
                  ${artwork.price?.toLocaleString()}
                </p>
                {inCart && PURCHASING_ENABLED ? (
                  <div
                    className="w-full py-4 text-center bg-[#F8F9FA] text-[#0A1128] text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-2"
                    style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500, letterSpacing: "0.32em" }}
                    data-testid="modal-in-cart-indicator"
                  >
                    <Check size={14} /> Added to Cart
                  </div>
                ) : PURCHASING_ENABLED ? (
                  <button
                    className="w-full bg-[#0a1929] text-white flex items-center justify-center gap-2 transition-all duration-300 hover:bg-[#CBA365] hover:text-[#0A1128] group/btn"
                    style={{
                      fontFamily: "'Outfit', sans-serif",
                      fontWeight: 500,
                      fontSize: "0.72rem",
                      letterSpacing: "0.32em",
                      textTransform: "uppercase",
                      padding: "1.15rem 2rem",
                    }}
                    data-testid="modal-add-to-cart-button"
                    onClick={() => { addItem(artwork); onClose(); }}
                  >
                    <ShoppingBag size={15} className="transition-transform duration-300 group-hover/btn:scale-110" /> Add to Cart
                  </button>
                ) : (
                  <button
                    className="inquire-sweep relative overflow-hidden w-full bg-[#0a1929] text-white flex items-center justify-center gap-2 group/btn"
                    style={{
                      fontFamily: "'Outfit', sans-serif",
                      fontWeight: 500,
                      fontSize: "0.72rem",
                      letterSpacing: "0.32em",
                      textTransform: "uppercase",
                      padding: "1.15rem 2rem",
                      transition: "color 0.4s ease",
                    }}
                    data-testid="modal-inquire-button"
                    onClick={() => setInquireOpen(true)}
                  >
                    <span
                      aria-hidden="true"
                      className="inquire-sweep__fill absolute inset-0"
                      style={{ backgroundColor: "#CBA365" }}
                    />
                    <span className="relative z-10 flex items-center gap-2">
                      <Mail size={15} /> Inquire
                    </span>
                  </button>
                )}
                <p
                  className="text-center mt-3"
                  style={{
                    fontFamily: "'Outfit', sans-serif",
                    fontWeight: 300,
                    fontSize: "0.72rem",
                    color: "rgba(10, 25, 41, 0.45)",
                  }}
                >
                  Contact us for shipping details and payment options
                </p>
              </>
            )}
          </div>
        </motion.div>

        {/* ── Mobile layout (< md) ─────────────────────────────────────────
            Completely independent tree from the desktop column layout above.
            Scrolls top-to-bottom: sticky frosted header → image (no crop) with
            zoom affordances → artist/title/price → description/metadata →
            CTA + shipping note + "More works by …" link. */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          onClick={(e) => e.stopPropagation()}
          className="md:hidden bg-white w-full max-h-[100vh] overflow-y-auto relative"
          data-testid="artwork-modal-content-mobile"
        >
          {/* 1. Sticky frosted header */}
          <div
            className="sticky top-0 z-20 flex justify-end"
            style={{
              background: "rgba(255, 255, 255, 0.85)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              borderBottom: "1px solid rgba(0,0,0,0.04)",
              padding: "0.75rem 1rem",
            }}
          >
            <button
              onClick={onClose}
              aria-label="Close"
              data-testid="artwork-modal-close-mobile"
              className="w-10 h-10 flex items-center justify-center text-[#0A1128]"
            >
              <X size={22} />
            </button>
          </div>

          {/* 2. Image area */}
          <div style={{ padding: "1rem 1.25rem 0.5rem" }}>
            <div
              className="relative"
              style={{ borderRadius: "2px", overflow: "hidden" }}
              onClick={() => setMobileZoomOpen(true)}
              role="button"
              aria-label="Open fullscreen zoom"
              data-testid="artwork-modal-image-wrapper-mobile"
            >
              <img
                src={activeUrl}
                alt={`${artwork.title} by ${artwork.artist_name}${hasMultiple ? ` (view ${activeImageIdx + 1} of ${images.length})` : ""}`}
                className="block w-full"
                style={{ maxHeight: "50vh", objectFit: "contain" }}
                draggable={false}
                data-testid="artwork-modal-image-mobile"
              />

              {/* Zoom button overlay (discoverable affordance) */}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setMobileZoomOpen(true); }}
                aria-label="Open fullscreen zoom"
                data-testid="artwork-modal-zoom-button-mobile"
                className="absolute bottom-3 right-3 flex items-center justify-center text-white"
                style={{
                  width: "42px",
                  height: "42px",
                  borderRadius: "9999px",
                  background: "rgba(15, 26, 58, 0.85)",
                  backdropFilter: "blur(6px)",
                  WebkitBackdropFilter: "blur(6px)",
                }}
              >
                <ZoomIn size={18} />
              </button>
            </div>

            {/* Thumbnail strip (multi-image pieces) */}
            {hasMultiple && (
              <div
                className="flex gap-2 overflow-x-auto mt-3 pb-1"
                data-testid="artwork-modal-thumbnails-mobile"
              >
                {images.map((img, i) => {
                  const active = i === activeImageIdx;
                  return (
                    <button
                      key={img.url + i}
                      type="button"
                      onClick={() => {
                        setActiveImageIdx(i);
                      }}
                      aria-label={`View image ${i + 1}`}
                      data-testid={`artwork-modal-thumbnail-mobile-${i}`}
                      className={`relative flex-shrink-0 w-16 h-16 bg-white overflow-hidden border transition-all duration-200 ${
                        active
                          ? "border-[#CBA365] opacity-100 ring-1 ring-[#CBA365]"
                          : "border-[#0A1128]/15 opacity-75"
                      }`}
                    >
                      <img src={img.url} alt="" className="w-full h-full object-cover" draggable={false} />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* 3. Artist / Title / Price */}
          <div style={{ padding: "1.5rem 1.5rem 1.25rem" }}>
            <p
              className="overline mb-2"
              style={{ color: "#CBA365", fontFamily: "'Outfit', sans-serif", fontWeight: 500, fontSize: "0.7rem", letterSpacing: "0.28em" }}
            >
              {artwork.artist_name}
            </p>
            <h2
              className="font-heading text-[#0A1128]"
              style={{
                fontSize: "2.75rem",
                fontWeight: 400,
                lineHeight: 1,
                letterSpacing: "-0.01em",
                textTransform: "none",
                WebkitTextStroke: "0",
                textShadow: "none",
              }}
              data-testid="artwork-modal-title-mobile"
            >
              {artwork.title}
            </h2>
            {!artwork.sold && artwork.price != null && (
              <p
                className="font-heading text-[#0A1128] mt-3"
                style={{
                  fontSize: "1.85rem",
                  fontWeight: 400,
                  lineHeight: 1,
                  textTransform: "none",
                  WebkitTextStroke: "0",
                  textShadow: "none",
                  letterSpacing: "0",
                }}
                data-testid="artwork-modal-price-mobile"
              >
                ${artwork.price.toLocaleString()}
              </p>
            )}
          </div>

          {/* 4. Description + metadata */}
          <div style={{ padding: "0 1.5rem" }}>
            <div style={{ height: "1px", margin: "1.5rem 0", background: "rgba(0,0,0,0.08)" }} />
            {artwork.description && (
              <div
                style={{
                  borderLeft: "1px solid #CBA365",
                  paddingLeft: "1rem",
                  marginBottom: "2rem",
                  fontFamily: "'Outfit', sans-serif",
                  fontStyle: "italic",
                  fontWeight: 300,
                  fontSize: "0.95rem",
                  lineHeight: 1.6,
                  color: "rgba(10, 25, 41, 0.75)",
                }}
              >
                {artwork.description}
              </div>
            )}

            {medium && (
              <div
                className="flex justify-between items-center"
                style={{ padding: "1rem 0", borderTop: "1px solid rgba(10, 25, 41, 0.08)" }}
              >
                <span
                  style={{
                    fontFamily: "'Outfit', sans-serif",
                    fontWeight: 500,
                    fontSize: "0.72rem",
                    letterSpacing: "0.28em",
                    textTransform: "uppercase",
                    color: "#CBA365",
                  }}
                >
                  Medium
                </span>
                <span
                  className="font-heading italic"
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 400,
                    color: "rgba(10, 25, 41, 0.75)",
                    textTransform: "none",
                    WebkitTextStroke: "0.35px currentColor",
                    textShadow: "0.35px 0 0 currentColor",
                    letterSpacing: "0",
                    textAlign: "right",
                  }}
                >
                  {medium}
                </span>
              </div>
            )}
            {artwork.dimensions && (
              <div
                className="flex justify-between items-center"
                style={{
                  padding: "1rem 0",
                  borderTop: "1px solid rgba(10, 25, 41, 0.08)",
                  borderBottom: "1px solid rgba(10, 25, 41, 0.08)",
                }}
              >
                <span
                  style={{
                    fontFamily: "'Outfit', sans-serif",
                    fontWeight: 500,
                    fontSize: "0.72rem",
                    letterSpacing: "0.28em",
                    textTransform: "uppercase",
                    color: "#CBA365",
                  }}
                >
                  Dimensions
                </span>
                <span
                  className="font-heading italic"
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 400,
                    color: "rgba(10, 25, 41, 0.75)",
                    textTransform: "none",
                    WebkitTextStroke: "0.35px currentColor",
                    textShadow: "0.35px 0 0 currentColor",
                    letterSpacing: "0",
                    textAlign: "right",
                  }}
                >
                  {artwork.dimensions}
                </span>
              </div>
            )}
            {framedLabel && (
              <div
                className="flex justify-between items-center"
                style={{
                  padding: "1rem 0",
                  borderBottom: "1px solid rgba(10, 25, 41, 0.08)",
                }}
                data-testid="modal-framed-row-mobile"
              >
                <span
                  style={{
                    fontFamily: "'Outfit', sans-serif",
                    fontWeight: 500,
                    fontSize: "0.72rem",
                    letterSpacing: "0.28em",
                    textTransform: "uppercase",
                    color: "#CBA365",
                  }}
                >
                  Framed
                </span>
                <span
                  className="font-heading italic"
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 400,
                    color: "rgba(10, 25, 41, 0.75)",
                    textTransform: "none",
                    WebkitTextStroke: "0.35px currentColor",
                    textShadow: "0.35px 0 0 currentColor",
                    letterSpacing: "0",
                    textAlign: "right",
                  }}
                >
                  {framedLabel}
                </span>
              </div>
            )}
          </div>

          {/* 5. CTA block */}
          <div style={{ padding: "1.5rem 1.5rem 2.5rem" }}>
            {artwork.sold ? (
              <div
                className="w-full py-4 text-center bg-[#F8F9FA] text-[#8A94A6] text-xs uppercase tracking-[0.2em]"
                data-testid="modal-sold-indicator-mobile"
              >
                Sold
              </div>
            ) : inCart && PURCHASING_ENABLED ? (
              <div
                className="w-full py-4 text-center bg-[#F8F9FA] text-[#0A1128] text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-2"
                style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500, letterSpacing: "0.32em" }}
                data-testid="modal-in-cart-indicator-mobile"
              >
                <Check size={14} /> Added to Cart
              </div>
            ) : PURCHASING_ENABLED ? (
              <button
                className="w-full bg-[#0a1929] text-white flex items-center justify-center gap-2 transition-all duration-300 hover:bg-[#CBA365] hover:text-[#0A1128]"
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  fontWeight: 500,
                  fontSize: "0.72rem",
                  letterSpacing: "0.32em",
                  textTransform: "uppercase",
                  padding: "1.15rem 2rem",
                }}
                data-testid="modal-add-to-cart-button-mobile"
                onClick={() => { addItem(artwork); onClose(); }}
              >
                <ShoppingBag size={15} /> Add to Cart
              </button>
            ) : (
              <button
                className="inquire-sweep relative overflow-hidden w-full bg-[#0a1929] text-white flex items-center justify-center gap-2"
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  fontWeight: 500,
                  fontSize: "0.72rem",
                  letterSpacing: "0.32em",
                  textTransform: "uppercase",
                  padding: "1.15rem 2rem",
                  transition: "color 0.4s ease",
                }}
                data-testid="modal-inquire-button-mobile"
                onClick={() => setInquireOpen(true)}
              >
                <span aria-hidden="true" className="inquire-sweep__fill absolute inset-0" style={{ backgroundColor: "#CBA365" }} />
                <span className="relative z-10 flex items-center gap-2">
                  <Mail size={15} /> Inquire
                </span>
              </button>
            )}

            <p
              className="text-center mt-4"
              style={{
                fontFamily: "'Outfit', sans-serif",
                fontWeight: 300,
                fontSize: "0.75rem",
                color: "rgba(10, 25, 41, 0.5)",
              }}
            >
              Contact us for shipping details and payment options
            </p>

            {artwork.artist_slug && artwork.artist_name && (
              <Link
                to={`/artists/${artwork.artist_slug}`}
                onClick={onClose}
                className="block text-center mt-8"
                data-testid="artwork-modal-more-works-link"
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  fontWeight: 500,
                  fontSize: "0.75rem",
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                  color: "#CBA365",
                }}
              >
                More works by{" "}
                <span style={{ borderBottom: "1px solid #CBA365", paddingBottom: "1px" }}>
                  {artwork.artist_name}
                </span>{" "}
                →
              </Link>
            )}
          </div>
        </motion.div>
      </motion.div>

      {/* ── Mobile fullscreen zoom overlay ─────────────────────────────── */}
      <AnimatePresence>
        {mobileZoomOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden fixed inset-0 z-[110] flex items-center justify-center"
            style={{ background: "rgba(10, 15, 30, 0.98)" }}
            onClick={() => setMobileZoomOpen(false)}
            data-testid="artwork-modal-zoom-overlay"
          >
            {/* Pinch + one-finger-pan gesture container. Clicking inside is
                swallowed so the overlay backdrop-tap only fires outside the
                transformed image area. */}
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ width: "100%", height: "100%" }}
            >
              <TransformWrapper
                initialScale={1}
                minScale={1}
                maxScale={5}
                centerOnInit
                doubleClick={{ mode: "toggle", step: 2 }}
                panning={{ velocityDisabled: false }}
                wheel={{ step: 0.12 }}
                pinch={{ step: 5 }}
              >
                <TransformComponent
                  wrapperStyle={{ width: "100%", height: "100%" }}
                  contentStyle={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  <img
                    src={activeUrl}
                    alt={artwork.title}
                    draggable={false}
                    style={{
                      maxWidth: "100vw",
                      maxHeight: "100vh",
                      objectFit: "contain",
                      userSelect: "none",
                      WebkitUserSelect: "none",
                    }}
                    data-testid="artwork-modal-zoom-image"
                  />
                </TransformComponent>
              </TransformWrapper>
            </div>

            <button
              type="button"
              onClick={() => setMobileZoomOpen(false)}
              aria-label="Close zoom"
              data-testid="artwork-modal-zoom-close"
              className="absolute top-4 right-4 flex items-center justify-center text-white"
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "9999px",
                background: "rgba(255,255,255,0.15)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
              }}
            >
              <X size={20} />
            </button>
            <span
              key={`zoom-hint-${artwork.id}-${activeImageIdx}`}
              className="artwork-zoom-hint absolute bottom-8 left-1/2 -translate-x-1/2 text-white"
              style={{
                background: "rgba(15, 26, 58, 0.85)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                padding: "8px 18px",
                borderRadius: "24px",
                fontFamily: "'Outfit', sans-serif",
                fontWeight: 500,
                fontSize: "0.7rem",
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
              }}
              data-testid="artwork-modal-zoom-instructions"
            >
              Pinch to zoom · Drag to pan
            </span>
          </motion.div>
        )}
      </AnimatePresence>
      <InquireModal
        isOpen={inquireOpen}
        onClose={() => setInquireOpen(false)}
        pieceName={artwork?.title}
        artistName={artwork?.artist_name}
      />
    </AnimatePresence>
  );
}
